import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, Calendar, PlayCircle, Loader2, Film, Tv as TvIcon, AlertCircle } from "lucide-react";
import api from "@/lib/api";

/**
 * Inline rich card embedded inside forum posts via [movie=...] / [tv=...] / [title=...].
 *
 * Resolution:
 *   1. If `mediaType` is "movie"|"tv" and `arg` looks numeric → treat as TMDB id, fetch details.
 *   2. Else treat the content (arg ?? children text) as a search query and use the first hit.
 *      "title" lets the result type be whichever matches first (movie OR tv).
 *
 * Renders: poster + title + year + rating + short overview + "Open in TorHub" CTA
 * (clicking it goes to `/?id=<tmdb_id>&type=<movie|tv>` which opens the DetailModal
 * straight onto the torrent section).
 */
export default function MovieEmbed({ arg, query, mediaType = "title" }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const run = async () => {
      setLoading(true); setError(false);
      try {
        const num = arg && /^\d+$/.test(String(arg).trim());
        if (num && mediaType !== "title") {
          const id = parseInt(arg, 10);
          const { data } = await api.get(`/details/${mediaType}/${id}`);
          if (alive) setData({ ...data, media_type: mediaType });
          return;
        }
        const q = (arg && !num) ? arg : (query || "");
        const { data: res } = await api.get("/search", { params: { q } });
        const results = Array.isArray(res?.results) ? res.results : (Array.isArray(res) ? res : []);
        const want = mediaType === "title" ? null : mediaType;
        const hit = results.find((r) => (!want || r.media_type === want)) || results[0];
        if (!hit) { if (alive) setError(true); return; }
        // Pull full details to get overview + backdrop
        const mt = hit.media_type || want || "movie";
        const detId = hit.id;
        try {
          const { data: det } = await api.get(`/details/${mt}/${detId}`);
          if (alive) setData({ ...det, media_type: mt });
        } catch {
          if (alive) setData({ ...hit, media_type: mt });
        }
      } catch {
        if (alive) setError(true);
      } finally {
        if (alive) setLoading(false);
      }
    };
    run();
    return () => { alive = false; };
  }, [arg, query, mediaType]);

  if (loading) {
    return (
      <span className="inline-flex items-center gap-2 my-2 px-3 py-2 bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded text-xs text-[var(--tt-text-muted)]">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading title…
      </span>
    );
  }
  if (error || !data) {
    return (
      <span className="inline-flex items-center gap-2 my-2 px-3 py-2 bg-red-500/10 border border-red-500/30 rounded text-xs text-red-300">
        <AlertCircle className="w-3.5 h-3.5" /> Couldn&apos;t find that title.
      </span>
    );
  }

  const id = data.id;
  const type = data.media_type;
  const title = data.title || data.name || "Untitled";
  // Backend returns `year` directly; fall back to TMDB date fields if present
  const year = data.year || (data.release_date || data.first_air_date || "").slice(0, 4);
  // Backend returns `poster` as a full URL; fall back to building from TMDB path
  const poster = data.poster
    || (data.poster_path ? `https://image.tmdb.org/t/p/w185${data.poster_path}` : null);
  // Backend returns `rating`; TMDB uses `vote_average`
  const ratingNum = data.rating != null ? Number(data.rating)
                   : data.vote_average != null ? Number(data.vote_average)
                   : null;
  const rating = ratingNum ? ratingNum.toFixed(1) : null;
  const overview = (data.overview || "").slice(0, 220);
  const TypeIcon = type === "tv" ? TvIcon : Film;

  return (
    <div
      data-testid={`movie-embed-${type}-${id}`}
      className="my-3 flex gap-4 bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-lg overflow-hidden max-w-xl"
      style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.35)" }}
    >
      {poster ? (
        <Link to={`/title/${type}/${id}`} className="shrink-0 block">
          <img src={poster} alt="" className="w-24 h-36 object-cover" />
        </Link>
      ) : (
        <div className="w-24 h-36 shrink-0 bg-gradient-to-br from-[var(--tt-primary)]/30 to-[var(--tt-purple)]/30 flex items-center justify-center">
          <TypeIcon className="w-8 h-8 text-white/60" />
        </div>
      )}
      <div className="flex-1 min-w-0 py-3 pr-3 flex flex-col">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-[var(--tt-purple)] mb-1">
          <TypeIcon className="w-3 h-3" />
          {type === "tv" ? "TV Series" : "Movie"}
        </div>
        <Link to={`/title/${type}/${id}`} className="block">
          <h4 className="font-display font-extrabold text-base text-[var(--tt-primary)] hover:underline truncate" title={title}>
            {title}
          </h4>
        </Link>
        <div className="flex items-center gap-3 text-[11px] text-[var(--tt-text-muted)] mt-1">
          {year && <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{year}</span>}
          {rating && <span className="inline-flex items-center gap-1 text-[var(--tt-yellow)] font-bold"><Star className="w-3 h-3" fill="currentColor" />{rating}</span>}
        </div>
        {overview && (
          <p className="text-[11px] text-white/75 leading-snug mt-1.5 line-clamp-3">{overview}{data.overview && data.overview.length > 220 ? "…" : ""}</p>
        )}
        <Link
          to={`/title/${type}/${id}`}
          data-testid={`movie-embed-open-${id}`}
          className="mt-auto inline-flex items-center gap-1.5 self-start bg-[var(--tt-primary)] hover:bg-[var(--tt-primary-strong)] text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full"
        >
          <PlayCircle className="w-3 h-3" /> Open Torrents
        </Link>
      </div>
    </div>
  );
}
